import { useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { Loader2, Phone, Plus, UserPlus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import type { Account, AccountContact } from '@/api/commercial/accounts';
import { accountApi } from '@/api/commercial/accounts';
import { ContactFormDialog } from '@/components/commercial/accounts/create/contact-form-dialog';
import { AccountCard } from '@/components/commercial/accounts/details/account-card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import type { LocalAccount } from '@/context/create-account-flow';
import { useContactMutations } from '@/hooks/use-contact-mutations';
import { useToast } from '@/hooks/use-toast';
import { ContactCardSelectable } from '../create/contact-card-selectable';

const formatCpfCnpj = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const PROPOSAL_CONTACT_FOR_OPTIONS = ['Faturamento', 'Marketing', 'Suporte', 'Comercial', 'Fiscal'];

interface StepClientSharedProps {
  account: Account | null;
  contacts: AccountContact[];
  selectedContactIds: Set<string>;
  proposalId?: string | null;
  isUpdate?: boolean;

  onAccountChange: (account: Account | null) => void;
  onContactsChange: (contacts: AccountContact[]) => void;
  onContactSelectionChange: (contactId: string) => void;
  onNextStep: () => void;
  onBack: () => void;

  onCreateAccount: () => void;
  onEditAccount: () => void;
}

export function StepClientShared({
  account,
  contacts,
  selectedContactIds,
  proposalId,
  isUpdate = false,
  onAccountChange,
  onContactsChange,
  onContactSelectionChange,
  onNextStep,
  onBack,
  onCreateAccount,
  onEditAccount,
}: StepClientSharedProps) {
  const { toast } = useToast();
  const [accountSearchResults, setAccountSearchResults] = useState<Account[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const [createContactModalOpen, setCreateContactModalOpen] = useState(false);
  const [editContactModalOpen, setEditContactModalOpen] = useState(false);
  const [deleteContactDialogOpen, setDeleteContactDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<AccountContact | null>(null);

  const { createContact, updateContact } = useContactMutations({
    accountId: account?.id,
    contacts,
    onContactsChange,
  });

  const debouncedSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        if (!value || value.trim().length === 0) {
          setAccountSearchResults([]);
          setIsSearching(false);
          return;
        }

        setIsSearching(true);
        try {
          const result = await accountApi.findMany({
            search: value,
            limit: 20,
          });
          setAccountSearchResults(result.docs);
        } catch (error) {
          console.error('Error searching accounts:', error);
          setAccountSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 500),
    [],
  );

  const handleSearchChange = useCallback(
    (queryText: string) => {
      debouncedSearch(queryText);
    },
    [debouncedSearch],
  );

  const handleAccountSelect = useCallback(
    (selected: Account[]) => {
      if (proposalId) {
        return;
      }

      if (selected.length > 0) {
        onAccountChange(selected[0]);
      } else {
        onAccountChange(null);
        onContactsChange([]);
      }
    },
    [onAccountChange, onContactsChange, proposalId],
  );

  const { isLoading: isContactsLoading, isFetching: isContactsFetching } = useQuery({
    queryKey: ['findContacts', account?.id],
    queryFn: async () => {
      if (!account?.id) return [];
      const result = await accountApi.findContacts(account.id);
      onContactsChange(result);
      return result;
    },
    enabled: !!account?.id,
  });

  const handleEditContact = (contact: AccountContact) => {
    setSelectedContact(contact);
    setEditContactModalOpen(true);
  };

  const handleDeleteContact = (contact: AccountContact) => {
    setSelectedContact(contact);
    setDeleteContactDialogOpen(true);
  };

  const confirmDeleteContact = async () => {
    if (!selectedContact?.id) return;

    try {
      await accountApi.deleteContact(selectedContact.id);

      const updatedContacts = contacts.filter((c) => c.id !== selectedContact.id);
      onContactsChange(updatedContacts);

      toast({
        title: 'Contato excluído',
        description: 'O contato foi excluído com sucesso!',
      });

      setDeleteContactDialogOpen(false);
      setSelectedContact(null);
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast({
        title: 'Erro',
        description: 'Ocorreu um erro ao excluir o contato. Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  const contactsLoading = isContactsLoading || isContactsFetching;

  const accountAsLocal: LocalAccount | null = account
    ? {
        id: account.id,
        document: account.document,
        name: account.name,
        rg: account.rg,
        social_name: account.social_name,
        fantasy_name: account.fantasy_name,
        state_registration: account.state_registration,
        municipal_registration: account.municipal_registration,
        status: account.status,
        situationIE: account.situationIE,
        typeIE: account.typeIE,
        setor: account.setor,
      }
    : null;

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <div className="flex gap-2 items-end w-[30vw]">
          <div className="flex-1">
            <div className="text-sm font-medium mb-1.5 block">Cliente</div>
            <Combobox
              disableLocalFilter
              type="single"
              data={accountSearchResults}
              placeholder="Digite o CPF, CNPJ, Razão Social ou Nome Fantasia..."
              value={account ? [account] : []}
              isLoading={isSearching}
              disabled={!!proposalId}
              onOptionChange={handleAccountSelect}
              keyExtractor={(item) => item?.document?.value ?? item?.id}
              displayValueGetter={(item) =>
                `${item?.social_name || item?.fantasy_name || item?.name || ''} - ${formatCpfCnpj(item?.document?.value || '')}`
              }
              onSearchChange={handleSearchChange}
            />
          </div>
          <Button
            variant="outline"
            type="button"
            onClick={onCreateAccount}
            disabled={!!proposalId}
            className="whitespace-nowrap"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Novo Cliente
          </Button>
        </div>
        <div className="flex gap-2 items-end justify-end">
          <Button variant="outline" type="button" onClick={onBack}>
            Voltar
          </Button>
        </div>
      </div>

      <div className="flex gap-4">
        {account && (
          <>
            <div className="w-[45vw]">
              <AccountCard
                account={accountAsLocal!}
                billingStatus={account.billing_status}
                billingSituation={account.billing_situation}
                onEdit={onEditAccount}
              />
            </div>

            <Card className="w-full">
              <CardHeader className="pb-3 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Phone className="h-5 w-5 text-primary" />
                    Contatos
                    <Badge variant="secondary" className="text-xs">
                      {contacts?.length || 0}
                    </Badge>
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCreateContactModalOpen(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <div className="lg:col-span-2 h-full">
                  {contactsLoading ? (
                    <div className="flex items-center justify-center h-full min-h-[160px]">
                      <Loader2 className="h-7 w-7 animate-spin text-primary" />
                    </div>
                  ) : contacts && contacts.length > 0 ? (
                    <div className="flex flex-wrap gap-x-1.5 gap-y-3">
                      {contacts.map((contact, idx) => (
                        <ContactCardSelectable
                          key={contact.id ?? idx}
                          contact={contact}
                          isSelected={selectedContactIds.has(contact.id)}
                          onSelectionChange={(selected) => {
                            if (selected !== selectedContactIds.has(contact.id)) {
                              onContactSelectionChange(contact.id);
                            }
                          }}
                          onEdit={() => handleEditContact(contact)}
                          onDelete={() => handleDeleteContact(contact)}
                          showEditActions={contact.originType !== 'api'}
                        />
                      ))}
                    </div>
                  ) : (
                    <Card>
                      <CardHeader>
                        <CardTitle>Nenhum contato encontrado</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">
                          Este cliente não possui contatos cadastrados.
                        </p>
                        <Button variant="outline" onClick={() => setCreateContactModalOpen(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          Adicionar Contato
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {selectedContactIds.size > 0 && (
        <div className="flex justify-end">
          <Button type="button" onClick={onNextStep}>
            Próxima etapa
          </Button>
        </div>
      )}

      <ContactFormDialog
        open={createContactModalOpen}
        onClose={() => setCreateContactModalOpen(false)}
        mode="create"
        contactForOptions={PROPOSAL_CONTACT_FOR_OPTIONS}
        onSubmit={(data) => createContact.mutate(data)}
      />

      {selectedContact && (
        <ContactFormDialog
          open={editContactModalOpen}
          onClose={() => {
            setEditContactModalOpen(false);
            setSelectedContact(null);
          }}
          mode="edit"
          contact={selectedContact}
          contactForOptions={PROPOSAL_CONTACT_FOR_OPTIONS}
          onSubmit={(data) => updateContact.mutate({ contactId: selectedContact.id, data })}
        />
      )}

      <AlertDialog open={deleteContactDialogOpen} onOpenChange={setDeleteContactDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Contato</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o contato "{selectedContact?.name}"? Esta ação não pode
              ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedContact(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteContact}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
